import { getRAUrlParams } from "features/Common/utils/url.utils";
import { INTEGRATION_ROUTE_GENERATORS } from "features/Integration/constants/Integration.urlPaths";
import { instagram } from "features/Integration/constants/Integration.constants";
import {
  getInstagramConnectCallbackParamKeys,
  INSTAGRAM_CONNECT_URL_PARAMS,
  INSTAGRAM_OAUTH_REDIRECT_ORIGIN,
} from "../constants/instagram.api";

const getInstagramIntegrationPath = () =>
  INTEGRATION_ROUTE_GENERATORS.integration_details({ slug: instagram });

/**
 * OAuth redirect target for Instagram connect.
 * Meta requires redirect_uri without a raw `#` in the authorize URL query string;
 * the value is URL-encoded when set via URLSearchParams.
 */
export const getInstagramOAuthRedirectUri = () =>
  `${INSTAGRAM_OAUTH_REDIRECT_ORIGIN}/`;

/**
 * BE may return an oauth_url where an unencoded `#` in redirect_uri splits the
 * authorize URL and drops params (e.g. response_type, scope) into the fragment.
 * Rebuild the URL with a properly encoded redirect_uri and recover lost params.
 */
export const fixInstagramOAuthUrl = (oauthUrlFromBe) => {
  if (!oauthUrlFromBe) return oauthUrlFromBe;

  const hashIndex = oauthUrlFromBe.indexOf("#");
  const baseUrl =
    hashIndex === -1 ? oauthUrlFromBe : oauthUrlFromBe.slice(0, hashIndex);
  const fragmentRecovery =
    hashIndex === -1 ? "" : oauthUrlFromBe.slice(hashIndex + 1);

  const url = new URL(baseUrl);
  url.searchParams.set("redirect_uri", getInstagramOAuthRedirectUri());

  if (fragmentRecovery) {
    const parts = fragmentRecovery.split("&");
    const startsWithRoute = parts[0]?.startsWith("/");
    const paramsToRecover = startsWithRoute ? parts.slice(1) : parts;

    paramsToRecover.forEach((part) => {
      const eqIndex = part.indexOf("=");
      if (eqIndex <= 0) return;

      const key = part.slice(0, eqIndex);
      if (url.searchParams.has(key)) return;

      const value = part.slice(eqIndex + 1);
      url.searchParams.set(key, decodeURIComponent(value.replace(/\+/g, " ")));
    });
  }

  return url.toString();
};

const mergeUrlParams = (...paramSources) => {
  const merged = new URLSearchParams();

  paramSources.forEach((source) => {
    if (!source) return;

    source.forEach((value, key) => {
      if (!merged.has(key)) {
        merged.set(key, value);
      }
    });
  });

  return merged;
};

const hasConnectRedirectParams = (urlParams) =>
  urlParams?.get(INSTAGRAM_CONNECT_URL_PARAMS.status) !== null;

/**
 * Read connect redirect params from hash-route query or pre-hash search
 * (when BE redirects to origin without the hash route).
 */
export const getInstagramConnectUrlParams = () => {
  const hashParams = getRAUrlParams();

  if (hasConnectRedirectParams(hashParams)) {
    return hashParams;
  }

  if (!window.location.search) {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);

  if (!hasConnectRedirectParams(searchParams)) {
    return null;
  }

  return mergeUrlParams(searchParams, hashParams);
};

/**
 * BE may land on `/?status=...` when redirect_uri is origin-only.
 * Forward to the Instagram integration hash route so connect handler runs.
 */
export const redirectInstagramOAuthLandingIfNeeded = () => {
  const { search, hash } = window.location;

  if (hash.includes(getInstagramIntegrationPath())) {
    return false;
  }

  if (!search) return false;

  const searchParams = new URLSearchParams(search);

  if (!hasConnectRedirectParams(searchParams)) {
    return false;
  }

  const params = new URLSearchParams();
  getInstagramConnectCallbackParamKeys().forEach((key) => {
    const value = searchParams.get(key);
    if (value !== null) {
      params.set(key, value);
    }
  });

  const target = `${
    window.location.origin
  }/#${getInstagramIntegrationPath()}?${params.toString()}`;
  window.location.replace(target);
  return true;
};
